/**
 * The frontend half of the social-platform registry.
 *
 * Everything a link's TYPE decides — its glyph, its brand hue, the placeholder
 * its input shows, whether a profile search exists for it — is read from the
 * generated bindings of `shared/config/social_platforms.config.json`. This file
 * holds only what a config file cannot: the name → component table for the
 * glyphs themselves, modelled on `shared/components/interactions/interactionTypes.tsx`,
 * which owns one for the same reason (lucide-react dropped brand marks).
 *
 * THE REGISTRY IS NOT AN ALLOW-LIST. `getSocialPlatformDisplay` takes a plain
 * `string`, because the column does: a platform this build has never heard of —
 * a tenant's own "Houzz", or a sixth entry added after this bundle shipped —
 * resolves to the generic link glyph under its own label rather than rendering
 * as nothing. That fallback is the feature, not defensive coding.
 */
import { Facebook, Instagram, Link as LinkIcon, Linkedin } from 'lucide-react';

import {
  SOCIAL_PLATFORM_VALUES,
  socialPlatformColor,
  socialPlatformDarkColor,
  socialPlatformIcon,
  socialPlatformLabel,
  socialPlatformSearchEndpoint,
  socialPlatformUrlPrefix,
  type SocialPlatform,
} from '../../../generated/config-enums';
import { PinterestIcon } from './PinterestIcon';
import { XIcon } from './XIcon';

/**
 * A glyph component. `style` is part of the contract because a brand hue is DATA
 * off the config — Tailwind cannot see a class built at runtime — so every call
 * site applies the colour as a value.
 */
type IconComponent = React.ComponentType<{ className?: string; style?: React.CSSProperties }>;

/**
 * Every glyph name the config may carry, plus the generic fallback.
 *
 * Exported so its own coverage test can assert the config names nothing this
 * table lacks — a miss would otherwise fall through to the generic link icon and
 * look exactly like a deliberate "unknown platform".
 *
 * This table is the ONE thing a new config entry may also need: a brand
 * lucide-react ships (`Youtube`, `Github`, `Slack`, …) or one reusing `Link`
 * costs nothing here, and a brand needing its own mark needs an SVG beside the
 * two below. The coverage test makes that a red build rather than a silent
 * fallback.
 */
export const SOCIAL_PLATFORM_ICON_COMPONENTS: Record<string, IconComponent> = {
  Linkedin,
  Instagram,
  Facebook,
  XBrand: XIcon,
  Pinterest: PinterestIcon,
};

/** The glyph an unregistered platform gets. Its own export so tests can name it. */
export const GENERIC_SOCIAL_ICON: IconComponent = LinkIcon;

/**
 * The class that reads the two custom properties `style` sets below.
 *
 * Static, so Tailwind can see it; the VALUES are data off the config, which
 * Tailwind never could. Doing the light/dark switch in CSS rather than by reading
 * the theme in React is what keeps this whole family free of a
 * `ThemeContextProvider` dependency — a display helper that needed one would drag
 * it into every test of every page that renders a link.
 */
export const SOCIAL_BRAND_COLOR_CLASS =
  'text-[color:var(--social-brand)] dark:text-[color:var(--social-brand-dark)]';

export interface SocialPlatformDisplay {
  /** The glyph component — never null; unknown platforms get the generic link. */
  Icon: IconComponent;
  /** What to call this link on screen. */
  name: string;
  /**
   * The brand's two hues, as CSS custom properties, or undefined for an
   * unregistered platform — which then inherits `currentColor`, i.e. the
   * surrounding text colour, rather than borrowing some other brand's.
   *
   * Both arms travel together because half these brands are unreadable against
   * the other theme (X's near-black on a dark card, LinkedIn's blue under it),
   * and `SOCIAL_BRAND_COLOR_CLASS` picks between them in CSS.
   */
  style: React.CSSProperties | undefined;
  /** Pair with `style`; empty when the platform has no brand hue to apply. */
  className: string;
}

const REGISTERED: ReadonlySet<string> = new Set(SOCIAL_PLATFORM_VALUES);

/** Whether the predefined registry declares this platform. Labelling only. */
export function isRegisteredSocialPlatform(platform: string): platform is SocialPlatform {
  return REGISTERED.has(platform);
}

/**
 * How to render one link.
 *
 * `label` is the entry's own display name. It WINS over the registry's, so a
 * tenant that stores a LinkedIn link under "Noa's profile" sees that — and it is
 * the only name an unregistered platform has.
 */
export function getSocialPlatformDisplay(
  platform: string,
  label: string | null | undefined
): SocialPlatformDisplay {
  if (!isRegisteredSocialPlatform(platform)) {
    return {
      Icon: GENERIC_SOCIAL_ICON,
      name: label || platform,
      style: undefined,
      className: '',
    };
  }
  const iconName = socialPlatformIcon[platform];
  return {
    Icon: SOCIAL_PLATFORM_ICON_COMPONENTS[iconName] ?? GENERIC_SOCIAL_ICON,
    name: label || socialPlatformLabel[platform],
    style: {
      '--social-brand': socialPlatformColor[platform],
      '--social-brand-dark': socialPlatformDarkColor[platform],
    } as React.CSSProperties,
    className: SOCIAL_BRAND_COLOR_CLASS,
  };
}

/**
 * The hint an empty input for this platform shows.
 *
 * A URL PREFIX, deliberately not a complete example — `ui-detail-and-form-pages.md`
 * § "Empty / placeholder hint style" bans a placeholder mistakable for a value,
 * and allows exactly this shape (`https://linkedin.com/in/…`).
 */
export function socialPlatformPlaceholder(platform: string): string | undefined {
  return isRegisteredSocialPlatform(platform)
    ? `${socialPlatformUrlPrefix[platform]}…`
    : undefined;
}

/**
 * The profile-search endpoint for this platform, or undefined where none exists.
 *
 * Only the two brands Google Custom Search has a social endpoint for carry one;
 * the config spells the rest as an empty string, which is a stated answer rather
 * than a missing key.
 */
export function socialPlatformSearchPath(platform: string): string | undefined {
  if (!isRegisteredSocialPlatform(platform)) return undefined;
  return socialPlatformSearchEndpoint[platform] || undefined;
}

/**
 * The part of a stored URL worth reading — its handle.
 *
 * A social URL is an opaque token whose only human-meaningful segment is the
 * tail: `https://www.linkedin.com/in/adam-gurfinkel-9b8b2a12` says nothing that
 * `adam-gurfinkel-9b8b2a12` does not, and the prefix costs the width of the whole
 * card to repeat once per link. So the compact view shows the handle and keeps
 * the full URL in the link's own tooltip and `href`.
 *
 * The registry's `url_prefix` is the yardstick, matched HOST-first and
 * case-insensitively so the shapes people actually paste — `http://`, `www.`, a
 * trailing slash — all reduce to the same handle. A URL whose host is NOT the
 * platform's keeps its host in the result (`example.com/x`), because a LinkedIn
 * entry pointing somewhere else is exactly what the reader needs to see.
 */
export function socialProfileHandle(platform: string, url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Not an absolute URL. Nothing to strip, and the raw value is the only
    // honest thing to show.
    return trimmed;
  }
  const host = parsed.host.replace(/^www\./i, '');
  const tail = (parsed.pathname + parsed.search + parsed.hash).replace(/\/+$/, '');

  if (isRegisteredSocialPlatform(platform)) {
    let prefix: URL | undefined;
    try {
      prefix = new URL(socialPlatformUrlPrefix[platform]);
    } catch {
      // A malformed prefix is a config bug; it must not blank the label.
      prefix = undefined;
    }
    if (prefix && host === prefix.host.replace(/^www\./i, '')) {
      const prefixPath = prefix.pathname.replace(/\/+$/, '');
      const rest =
        prefixPath && tail.toLowerCase().startsWith(`${prefixPath.toLowerCase()}/`)
          ? tail.slice(prefixPath.length)
          : tail;
      // A bare `https://x.com/` has no handle at all; its host is what is left.
      return rest.replace(/^\/+/, '') || host;
    }
  }

  const rest = tail.replace(/^\/+/, '');
  return rest ? `${host}/${rest}` : host;
}
